import { type DefaultRootState } from '@databases/redux';
import { type DatabaseSnapshot } from '@databases/redux-database-adapter';
import { type Database } from '@databases/types/database';
import {
  type DatabaseView,
  type DatabaseViewSettings,
} from '@databases/types/database/view';

type CreateSelectionsDatabaseSnapshotGetterOptions = {
  getState: () => DefaultRootState;
  getView: () => DatabaseView;
  selectDatabase: (state: DefaultRootState, options: { contentId: string }) => Database;
  applyView: (
    savedDatabase: Database,
    database: Database,
    viewSettings: DatabaseViewSettings,
    state: DefaultRootState,
  ) => DatabaseSnapshot;
  projectEntryIds?: (input: {
    contentId: string;
    entryIds: readonly string[];
    state: DefaultRootState;
    viewId: string;
    viewSettings: DatabaseViewSettings;
  }) => readonly string[];
};

type CachedDatabaseSnapshot = {
  contentId: string;
  state: DefaultRootState;
  view: DatabaseView;
  snapshot: DatabaseSnapshot;
};

/**
 * Creates the view-aware database snapshot getter used by the selections adapter.
 *
 * Databases application state is already the Yjs-backed saved database, so selections do
 * not need the shared draft selector's empty-changeset projection. Persisted entry and field
 * order comes from that database because the active UI view snapshot can lag the local Yjs
 * transaction that inserted or moved an item. Other view settings remain UI-owned so unsaved
 * filters, sorts, search, and layout continue to affect the selection projection.
 */
export function createSelectionsDatabaseSnapshotGetter({
  getState,
  getView,
  selectDatabase,
  applyView,
  projectEntryIds,
}: CreateSelectionsDatabaseSnapshotGetterOptions) {
  let cached: CachedDatabaseSnapshot | undefined;

  return (contentId: string): DatabaseSnapshot => {
    const state = getState();
    const view = getView();

    if (
      cached !== undefined &&
      cached.contentId === contentId &&
      cached.state === state &&
      cached.view === view
    ) {
      return cached.snapshot;
    }

    const database = selectDatabase(state, { contentId });
    const persistedView = database.views.find(({ id }) => id === view.id);
    const viewSettings = persistedView
      ? {
          ...view.settings,
          entryOrder: persistedView.settings.entryOrder,
          fieldOrder: persistedView.settings.fieldOrder,
        }
      : view.settings;
    const viewSnapshot = applyView(database, database, viewSettings, state);
    const entryIds = projectEntryIds?.({
      contentId,
      entryIds: viewSnapshot.entries,
      state,
      viewId: view.id,
      viewSettings,
    });
    const snapshot =
      entryIds && entryIds !== viewSnapshot.entries
        ? { ...viewSnapshot, entries: entryIds }
        : viewSnapshot;

    cached = { contentId, state, view, snapshot };

    return snapshot;
  };
}
